import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../providers/LocaleProvider';
import { useLocalizedContent } from '../hooks/useLocalizedContent';
import type { LucideIcon } from 'lucide-react';
import { ArrowLeft, ArrowRight, Heart, LayoutDashboard, Loader2 } from 'lucide-react';
import {
  contentService,
  ContentBlockDto,
  ContentBlockType,
  EnhancedQuizResultDto,
  LessonDetailDto
} from '../services/contentService';
import { useNotifications } from './feedback/NotificationSystem';
import { notifyGamificationAfterAction } from '../utils/gamificationNotif';
import {
  isQuizResultPassed,
  patchLessonExerciseCompleted,
  patchLessonQuizPassed
} from '../utils/patchLessonProgress';
import { isContentMissingError } from '../utils/isContentMissingError';
import { PersianOverlayLoader } from './shared/PersianLoader';
import { ContentBlockDisplay, assignLessonBlockTourIds } from './learning/LessonViewer';
import { useLessonTypographyPreferences } from '../hooks/useLessonTypographyPreferences';
import { QuizModal } from './learning/QuizModal';
import { ExerciseModal, ExerciseType } from './learning/ExerciseModal';
import { subscriptionService } from '../services/subscriptionService';
import { SubscriptionPlan } from '../types/subscription';
import { ExerciseLockedModal } from './shared/ExerciseLockedModal';
import { LessonJourneyTimeline } from './learning/LessonJourneyTimeline';
import { resolveLessonQuizzes } from '../utils/lessonJourneySteps';
import {
  buildTagJourneySteps,
  readTagItemViewed,
  tagJourneyGatesClear,
  writeTagItemViewed
} from '../utils/tagJourneySteps';
import { useLessonContentViewTracking } from '../utils/lessonContentViewTracking';
import { localeMirroredNavArrowClass } from '../utils/localeNavArrow';
import { isDesignedForNonFarsiUsers } from '../utils/nonFarsiContentHint';
import { useNonFarsiContentGate } from '../hooks/useNonFarsiContentGate';
import { NonFarsiContentHintModal } from './learning/NonFarsiContentHintModal';
import { EnglishLockedContentHintIcon } from './shared/EnglishLockedContentHintIcon';
import { useRefreshStickyAudioMap, useStickyAudio } from '../contexts/StickyAudioContext';
import { WordHelpProvider } from '../contexts/WordHelpContext';
import { LessonPracticeQuizExpandables } from './learning/LessonPracticeQuizExpandables';
import { PassedLessonBanner } from './learning/PassedLessonBanner';
import { favoriteService } from '../services/favoriteService';
import { commentService } from '../services/commentService';
import { formatRatingOneDecimal, ratingAverageFromSummary } from '../utils/formatRating';
import { LearnerHeroViewFavoriteCluster } from './shared/LearnerHeroViewFavoriteCluster';
import { LearnerRatingCommentsTrigger } from './shared/LearnerRatingCommentsTrigger';
import { CommentModal } from './learning/CommentModal';
import { LessonPageHero } from './learning/LessonPageHero';
import { SimplePageHeader } from './shared/SimplePageHeader';
import { useLearnerHomeDesign } from '../utils/learnerHomeDesign';
import { lessonHeroEyebrow } from '../utils/lessonHeroLabel';

function splitLessonPages(blocks: ContentBlockDto[]): ContentBlockDto[][] {
  const lessonPages: ContentBlockDto[][] = [];
  let currentPageBlocks: ContentBlockDto[] = [];

  const orderedBlocks = [...blocks].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  orderedBlocks.forEach((block) => {
    if (block.type === ContentBlockType.PageSeparator) {
      if (currentPageBlocks.length > 0) {
        lessonPages.push(currentPageBlocks);
        currentPageBlocks = [];
      }
    } else {
      currentPageBlocks.push(block);
    }
  });

  if (currentPageBlocks.length > 0) {
    lessonPages.push(currentPageBlocks);
  }

  if (lessonPages.length === 0) {
    lessonPages.push(orderedBlocks.filter((b) => b.type !== ContentBlockType.PageSeparator));
  }

  return lessonPages;
}

export interface TagLessonViewerPanelProps {
  tagId: number;
  lessonId: number;
  isPassedForTag: boolean;
  onLessonProgressUpdated: () => void;
  onTagPassedChange?: (passed: boolean) => void;
  /** Tag name shown under the lesson number. */
  tagName: string;
  /** 1-based place in the tag's saved order. */
  tagLessonNumber?: number | null;
  /** Tag description shown under the lesson title in the hero. */
  tagDescription?: string | null;
  /** Tag icon for the hero badge + watermark. */
  tagIcon?: LucideIcon;
  onBack?: () => void;
  onBackToDashboard?: () => void;
  /** Tag completion progress shown under the hero description. */
  progress?: React.ReactNode;
  /** Rendered under the hero and above the lesson journey (about / filter). */
  betweenHeroAndJourney?: React.ReactNode;
}

export const TagLessonViewerPanel: React.FC<TagLessonViewerPanelProps> = ({
  tagId,
  lessonId,
  isPassedForTag,
  onLessonProgressUpdated,
  onTagPassedChange,
  tagName,
  tagLessonNumber,
  tagDescription,
  tagIcon,
  onBack,
  onBackToDashboard,
  progress,
  betweenHeroAndJourney
}) => {
  useRefreshStickyAudioMap();
  const { map: stickyAudioMap } = useStickyAudio();
  const { t } = useTranslation(['learning', 'notifications']);
  const { direction, locale } = useAppLocale();
  const isRtl = direction === 'rtl';
  const [homeDesign] = useLearnerHomeDesign();
  const simpleHome = homeDesign === 'mme';
  const { showError, showSuccess } = useNotifications();
  const lessonTypography = useLessonTypographyPreferences();
  const navArrowClass = (size: string) => localeMirroredNavArrowClass(isRtl, size);

  const [lesson, setLesson] = useState<LessonDetailDto | null>(null);
  const lessonRef = useRef<LessonDetailDto | null>(null);
  lessonRef.current = lesson;
  const lessonForceEnglish = lesson != null && isDesignedForNonFarsiUsers(lesson);
  const {
    gateState: nonFarsiGateState,
    tryOpenPendingHint: tryOpenNonFarsiPendingHint,
    openHintManually: openNonFarsiHintManually,
    handleDismiss: handleNonFarsiGateDismiss,
    handleContinue: handleNonFarsiGateContinue,
    isRtl: nonFarsiGateRtl,
  } = useNonFarsiContentGate();
  const { pickTitle: pickUiTitle } = useLocalizedContent();
  const [pages, setPages] = useState<ContentBlockDto[][]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [activeQuizId, setActiveQuizId] = useState<number | null>(null);
  const quizModalTargetIdRef = useRef<number | null>(null);
  const activeExerciseIdRef = useRef<number | null>(null);
  const [selectedExercise, setSelectedExercise] = useState<{ id: number; type: ExerciseType } | null>(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [userPlanIds, setUserPlanIds] = useState<number[]>([]);
  const [lockedExerciseModal, setLockedExerciseModal] = useState<{
    exerciseTitle: string;
    requiredPlans: { id: number; name: string; description?: string }[];
  } | null>(null);
  const [markingReview, setMarkingReview] = useState(false);
  const [passedForTag, setPassedForTag] = useState(isPassedForTag);
  const [tagViewed, setTagViewed] = useState(() => readTagItemViewed(tagId, `lesson:${lessonId}`, isPassedForTag));
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteToggling, setFavoriteToggling] = useState(false);
  const [lessonRatingAvg, setLessonRatingAvg] = useState<number | null>(null);
  const [lessonRatingCount, setLessonRatingCount] = useState(0);
  const [lessonRatingLoading, setLessonRatingLoading] = useState(true);
  const [journeyCollapseRequest, setJourneyCollapseRequest] = useState(0);

  const { markBlockViewed, trackBlocksOnSinglePage } =
    useLessonContentViewTracking(lessonId, pages, currentPage);
  const reviewableBlockViewedProp =
    trackBlocksOnSinglePage ? { onReviewableBlockViewed: markBlockViewed } : {};

  const loadAbortRef = useRef<AbortController | null>(null);

  const loadLesson = useCallback(async (options?: {
    preservePage?: boolean;
    silent?: boolean;
    merge?: (lesson: LessonDetailDto) => LessonDetailDto;
  }) => {
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;

    try {
      if (!options?.silent) setLoading(true);
      let lessonData = await contentService.getLessonDetail(lessonId, { signal: controller.signal });
      if (controller.signal.aborted) return;
      if (options?.merge) {
        lessonData = options.merge(lessonData);
      }

      setLesson(lessonData);
      const lessonPages = splitLessonPages(lessonData.contentBlocks);
      setPages(lessonPages);
      if (options?.preservePage) {
        setCurrentPage((prev) => Math.min(prev, Math.max(0, lessonPages.length - 1)));
      } else {
        setCurrentPage(0);
      }
    } catch (e) {
      if (controller.signal.aborted) return;
      showError(isContentMissingError(e) ? t('lesson.noLongerAvailable') : (e instanceof Error ? e.message : t('lesson.loadFailed')));
      setLesson(null);
      setPages([]);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [lessonId, showError]);

  const loadLessonRatingSummary = useCallback(async () => {
    if (!lessonId) {
      setLessonRatingAvg(null);
      setLessonRatingCount(0);
      setLessonRatingLoading(false);
      return;
    }
    setLessonRatingAvg(null);
    setLessonRatingCount(0);
    setLessonRatingLoading(true);
    try {
      const s = await commentService.getLessonRatingSummary(lessonId);
      const avg = ratingAverageFromSummary(s);
      if (s.ratingCount > 0 && avg != null) {
        setLessonRatingAvg(avg);
        setLessonRatingCount(s.ratingCount);
      } else {
        setLessonRatingAvg(null);
        setLessonRatingCount(0);
      }
    } catch {
      setLessonRatingAvg(null);
      setLessonRatingCount(0);
    } finally {
      setLessonRatingLoading(false);
    }
  }, [lessonId]);

  useEffect(() => {
    void loadLessonRatingSummary();
  }, [loadLessonRatingSummary]);

  useEffect(() => {
    const handleBeforePageTour = () => {
      setJourneyCollapseRequest((n) => n + 1);
    };

    window.addEventListener('app:beforeRequestPageTour', handleBeforePageTour);
    return () => window.removeEventListener('app:beforeRequestPageTour', handleBeforePageTour);
  }, []);

  useEffect(() => {
    if (!lesson || lesson.id !== lessonId) return;
    tryOpenNonFarsiPendingHint(lesson, 'lesson', lessonId);
  }, [lessonId, lesson, tryOpenNonFarsiPendingHint]);

  useEffect(() => {
    setPassedForTag(isPassedForTag);
  }, [isPassedForTag, lessonId]);

  useEffect(() => {
    const switching = lessonRef.current != null;
    if (switching) {
      setShowQuizModal(false);
      setSelectedExercise(null);
      setLockedExerciseModal(null);
    }
    void loadLesson(switching ? { silent: true } : undefined);
    return () => loadAbortRef.current?.abort();
  }, [loadLesson]);

  useEffect(() => {
    let cancelled = false;
    const loadTagFavoriteState = async () => {
      try {
        const ids = await favoriteService.getFavoriteTagLessonIds(tagId);
        if (!cancelled) setIsFavorite(ids.includes(lessonId));
      } catch {
        if (!cancelled) setIsFavorite(false);
      }
    };
    void loadTagFavoriteState();
    return () => {
      cancelled = true;
    };
  }, [tagId, lessonId]);

  const applyLocalLessonPatch = useCallback((patch: (l: LessonDetailDto) => LessonDetailDto) => {
    setLesson((prev) => (prev ? patch(prev) : prev));
  }, []);

  const refreshLessonProgress = useCallback(
    async (patch?: (l: LessonDetailDto) => LessonDetailDto) => {
      if (patch) {
        applyLocalLessonPatch(patch);
      }
      await loadLesson({ preservePage: true, silent: true, merge: patch });
    },
    [loadLesson, applyLocalLessonPatch]
  );

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const [plans, status] = await Promise.all([
          subscriptionService.getPlans(),
          subscriptionService.getSubscriptionStatus().catch(() => null)
        ]);
        setSubscriptionPlans(plans);
        const planId = status?.currentSubscription?.subscriptionPlan?.id;
        setUserPlanIds(planId != null ? [planId] : []);
      } catch {
        setSubscriptionPlans([]);
        setUserPlanIds([]);
      }
    };
    void loadPlans();
  }, [lessonId]);

  useEffect(() => {
    setTagViewed(readTagItemViewed(tagId, `lesson:${lessonId}`, isPassedForTag));
  }, [tagId, lessonId]);

  useEffect(() => {
    if (!lesson) {
      setActiveQuizId(null);
      quizModalTargetIdRef.current = null;
      return;
    }
    const quizzes = resolveLessonQuizzes(lesson);
    const firstId = quizzes[0]?.id ?? null;
    setActiveQuizId(firstId);
    quizModalTargetIdRef.current = firstId;
  }, [lesson?.id]);

  useEffect(() => {
    if (!lesson || lesson.id !== lessonId) return;
    const mandatoryExercises = (lesson.exercises ?? []).filter((exercise) => exercise.mandatoryForPassingLesson === true);
    const mandatoryQuizzes = resolveLessonQuizzes(lesson).filter((quiz) => quiz.mandatoryForPassingLesson === true);
    const shouldPass = tagViewed && tagJourneyGatesClear(mandatoryExercises, mandatoryQuizzes);
    if (shouldPass === passedForTag) return;
    let cancelled = false;
    void (async () => {
      try {
        await contentService.setTagLessonProgress(tagId, lessonId, shouldPass);
        if (cancelled) return;
        setPassedForTag(shouldPass);
        onTagPassedChange?.(shouldPass);
        if (shouldPass) notifyGamificationAfterAction();
        void onLessonProgressUpdated();
      } catch (error) {
        if (!cancelled) {
          showError(error instanceof Error ? error.message : t('lesson.updateProgressFailed'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lesson, tagViewed, passedForTag, tagId, lessonId, onTagPassedChange, onLessonProgressUpdated, showError, t]);

  const handleStartExercise = async (ex: { id: number; title: string; subscriptionPlanIds?: number[] }) => {
    const requiredPlanIds = ex.subscriptionPlanIds ?? [];
    const hasAccess =
      requiredPlanIds.length === 0 || requiredPlanIds.some((id) => userPlanIds.includes(id));
    if (!hasAccess) {
      const requiredPlans = requiredPlanIds
        .map((id) => subscriptionPlans.find((p) => p.id === id))
        .filter((p): p is SubscriptionPlan => !!p)
        .map((p) => ({ id: p.id, name: p.name, ...(p.description != null && { description: p.description }) }));
      setLockedExerciseModal({ exerciseTitle: ex.title, requiredPlans });
      return;
    }
    try {
      await contentService.getLessonExerciseForLearner(ex.id);
      activeExerciseIdRef.current = ex.id;
      setSelectedExercise({ id: ex.id, type: 'lesson' });
    } catch (err) {
      showError(err instanceof Error ? err.message : t('exercise.loadFailed'));
    }
  };

  const handleMarkViewed = () => {
    if (!lesson || tagViewed) return;
    setMarkingReview(true);
    writeTagItemViewed(tagId, `lesson:${lessonId}`, true);
    setTagViewed(true);
    setMarkingReview(false);
    showSuccess(t('journey.markedViewed', { defaultValue: 'Marked as viewed' }));
  };

  const handleUnmarkViewed = () => {
    if (!lesson || !tagViewed) return;
    setMarkingReview(true);
    writeTagItemViewed(tagId, `lesson:${lessonId}`, false);
    setTagViewed(false);
    setMarkingReview(false);
    showSuccess(t('journey.unmarkedViewed', { defaultValue: 'Marked as not viewed' }));
  };

  const handleQuizComplete = (result: EnhancedQuizResultDto) => {
    const quizId = quizModalTargetIdRef.current ?? activeQuizId;
    if (isQuizResultPassed(result) && quizId != null) {
      applyLocalLessonPatch((l) => patchLessonQuizPassed(l, quizId, result.score));
    }
    setShowQuizModal(false);
    onLessonProgressUpdated();
  };

  const handleQuizModalClose = () => {
    setShowQuizModal(false);
  };

  if (loading && !lesson) {
    return (
      <div className="relative min-h-[12rem]">
        <PersianOverlayLoader isVisible message={t('lesson.loading')} />
      </div>
    );
  }

  if (!lesson) {
    return (
      <p className="text-center text-sm text-gray-600 dark:text-gray-400 py-8">{t('tagLesson.loadFailed')}</p>
    );
  }

  if (lesson.id !== lessonId) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" aria-busy="true" aria-live="polite">
        <Loader2 className="h-8 w-8 animate-spin text-violet-600 dark:text-violet-300" aria-hidden />
      </div>
    );
  }

  const exercises = (lesson.exercises ?? []).filter((e) => !e.isInlineInLessonBody);
  const lessonQuizzes = resolveLessonQuizzes(lesson);
  const activeQuizForceEnglish =
    activeQuizId != null &&
    isDesignedForNonFarsiUsers(lessonQuizzes.find((q) => q.id === activeQuizId));
  const activeExerciseForceEnglish =
    selectedExercise != null &&
    isDesignedForNonFarsiUsers(lesson.exercises?.find((e) => e.id === selectedExercise.id));
  const onLastPage = currentPage === pages.length - 1;

  const mandatoryExercises = (lesson.exercises ?? []).filter((exercise) => exercise.mandatoryForPassingLesson === true);
  const mandatoryQuizzes = lessonQuizzes.filter((quiz) => quiz.mandatoryForPassingLesson === true);
  const journeySteps = buildTagJourneySteps({
    viewed: tagViewed,
    passed: tagViewed && tagJourneyGatesClear(mandatoryExercises, mandatoryQuizzes),
    markingView: markingReview,
    exercises: mandatoryExercises,
    quizzes: mandatoryQuizzes,
    onMarkViewed: handleMarkViewed,
    onUnmarkViewed: handleUnmarkViewed,
    onOpenExercise: (id) => {
      const ex = lesson.exercises?.find((e) => e.id === id);
      if (ex) void handleStartExercise({ id: ex.id, title: ex.title, subscriptionPlanIds: ex.subscriptionPlanIds ?? [] });
    },
    onOpenQuiz: (quizId) => {
      quizModalTargetIdRef.current = quizId;
      setActiveQuizId(quizId);
      setShowQuizModal(true);
    },
    jt: (key, opts) => t(key, opts as Record<string, unknown>),
    locale,
  });

  const lessonRatingLabel = formatRatingOneDecimal(lessonRatingAvg);
  const showEnglishLockedHintIcon = locale !== 'en' && lessonForceEnglish;
  const nonFarsiLessonHintClick = () => openNonFarsiHintManually('lesson', lessonId);
  const lessonTitle = pickUiTitle(lesson);
  const lessonEyebrow = lessonHeroEyebrow(
    t,
    tagLessonNumber != null && tagLessonNumber > 0 ? tagLessonNumber : lesson.lessonNumber,
    locale
  );
  const handleHeroBack = onBack ?? (() => window.history.back());

  const favoriteButton = (
    <button
      type="button"
      onClick={async () => {
        if (favoriteToggling || !lessonId) return;
        setFavoriteToggling(true);
        try {
          if (isFavorite) {
            await favoriteService.removeFavoriteTagLesson(tagId, lessonId);
            setIsFavorite(false);
            showSuccess(t('notifications:favorites.removed'));
          } else {
            await favoriteService.addFavoriteTagLesson(tagId, lessonId);
            setIsFavorite(true);
            showSuccess(t('notifications:favorites.added'));
          }
        } catch (e) {
          showError(e instanceof Error ? e.message : t('notifications:favorites.updateFailed'));
        } finally {
          setFavoriteToggling(false);
        }
      }}
      disabled={favoriteToggling}
      className="group inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-md border-0 bg-transparent p-0 text-inherit outline-none transition-transform hover:scale-105 active:scale-95 disabled:pointer-events-none disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-rose-400/80 focus-visible:ring-offset-1 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900"
      style={{ WebkitTapHighlightColor: 'transparent' }}
      title={
        favoriteToggling
          ? 'Updating favorite'
          : isFavorite
            ? t('lesson.removeFromFavorites')
            : t('lesson.addToFavorites')
      }
      aria-label={
        favoriteToggling
          ? 'Updating favorite'
          : isFavorite
            ? t('lesson.removeFromFavorites')
            : t('lesson.addToFavorites')
      }
      aria-busy={favoriteToggling}
    >
      {favoriteToggling ? (
        <Loader2 className="pointer-events-none h-4 w-4 animate-spin text-rose-500 dark:text-rose-400" aria-hidden />
      ) : (
        <Heart
          className={
            isFavorite
              ? 'pointer-events-none h-4 w-4 fill-rose-500 text-rose-600 dark:fill-rose-500 dark:text-rose-400'
              : 'pointer-events-none h-4 w-4 text-gray-400 transition-colors group-hover:text-rose-500 dark:text-gray-500 dark:group-hover:text-rose-400'
          }
          aria-hidden
        />
      )}
    </button>
  );

  const passedBanner = passedForTag ? (
    <PassedLessonBanner label={t('lesson.passedSuccessfullyBanner')} />
  ) : null;

  const resolvedTagDescription =
    (tagDescription ?? '').trim() || t('tagViewer.heroSubtitle', { ns: 'learning' });

  return (
    <WordHelpProvider blocks={lesson.contentBlocks ?? []} scopeKind="lesson" lessonId={lesson.id} loadRemote>
    <div className="space-y-4">
      {simpleHome ? (
        <>
          <SimplePageHeader
            tourId="tag-content-header"
            eyebrow={lessonEyebrow}
            eyebrowDetail={tagName}
            title={lessonTitle}
            onBack={handleHeroBack}
            backLabel={t('lesson.goBack')}
            above={
              <div className="flex min-w-0 flex-nowrap items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                <LearnerRatingCommentsTrigger
                  onOpenComments={() => setShowCommentModal(true)}
                  loading={lessonRatingLoading}
                  average={lessonRatingAvg}
                  ratingLabel={lessonRatingLabel}
                  ratingCount={lessonRatingCount}
                  accent="blue"
                />
                </div>
                <LearnerHeroViewFavoriteCluster viewCount={lesson.viewCount ?? 0} isRtl={isRtl}>
                  {favoriteButton}
                </LearnerHeroViewFavoriteCluster>
              </div>
            }
          />
          {progress}
        </>
      ) : (
      <LessonPageHero
        tourId="tag-content-header"
        compactChrome
        eyebrow={lessonEyebrow}
        eyebrowDetail={tagName}
        title={lessonTitle}
        description={resolvedTagDescription}
        compactDescription
        {...(tagIcon ? { icon: tagIcon } : {})}
        isPassed={passedForTag}
        passedAriaLabel={t('tagLesson.passedForTagAria')}
        topBar={
          <>
            <LearnerRatingCommentsTrigger
              onOpenComments={() => setShowCommentModal(true)}
              loading={lessonRatingLoading}
              average={lessonRatingAvg}
              ratingLabel={lessonRatingLabel}
              ratingCount={lessonRatingCount}
              accent="blue"
            />
            <LearnerHeroViewFavoriteCluster viewCount={lesson.viewCount ?? 0} isRtl={isRtl}>
              {favoriteButton}
            </LearnerHeroViewFavoriteCluster>
          </>
        }
        belowIcon={
          <button
            type="button"
            onClick={handleHeroBack}
            className="inline-flex h-5 w-8 items-center justify-center rounded-md border border-violet-200/90 bg-white/70 text-violet-950 shadow-sm backdrop-blur-[2px] transition-all hover:border-violet-300 hover:bg-white/90 active:scale-[0.98] dark:border-violet-700/50 dark:bg-violet-950/40 dark:text-violet-50 dark:hover:border-violet-600/60 dark:hover:bg-violet-950/55 md:w-9"
            aria-label={t('lesson.goBack')}
            title={t('lesson.goBack')}
          >
            <ArrowLeft className={`h-3.5 w-3.5 ${isRtl ? 'rotate-180' : ''}`} aria-hidden />
          </button>
        }
        actionPlacement="underBack"
        underIconExtra={
          showEnglishLockedHintIcon ? (
            <EnglishLockedContentHintIcon onClick={nonFarsiLessonHintClick} />
          ) : undefined
        }
        progress={progress}
        action={
          onBackToDashboard ? (
            <button
              type="button"
              onClick={onBackToDashboard}
              className="inline-flex h-5 w-8 items-center justify-center rounded-md border border-violet-200/90 bg-white/70 text-violet-950 shadow-sm backdrop-blur-[2px] transition-all hover:border-violet-300 hover:bg-white/90 active:scale-[0.98] dark:border-violet-700/50 dark:bg-violet-950/40 dark:text-violet-50 dark:hover:border-violet-600/60 dark:hover:bg-violet-950/55 md:w-9"
              aria-label={t('catalog.backToDashboard')}
              title={t('catalog.backToDashboard')}
            >
              <LayoutDashboard className="h-3.5 w-3.5" aria-hidden />
            </button>
          ) : undefined
        }
      />
      )}

      {betweenHeroAndJourney}

      <div className="space-y-4">
        {passedBanner}
        <LessonJourneyTimeline
          key={lesson.id}
          steps={journeySteps}
          persistExpandLessonId={lessonId}
          collapseRequest={journeyCollapseRequest}
        />
      </div>

      {pages.length >= 1 ? (
        <>
          <div className="space-y-4">
            {(() => {
              const blocks = pages[currentPage] ?? [];
              const blockTourIds = assignLessonBlockTourIds(blocks, locale, stickyAudioMap, 'tag-content-word-help');
              return blocks.map((block, index) => {
                const tourIds = blockTourIds[index] ?? {};
                return (
              <ContentBlockDisplay
                key={block.id || index}
                block={block}
                lessonId={lesson.id}
                pinWordHelp={blocks.length <= 1}
                lessonTypography={lessonTypography}
                lessonExercises={lesson.exercises ?? []}
                userPlanIds={userPlanIds}
                subscriptionPlans={subscriptionPlans}
                onStartInlineExercise={(exerciseId) => {
                  const ex = lesson.exercises?.find((e) => e.id === exerciseId);
                  if (!ex) return;
                  void handleStartExercise({
                    id: ex.id,
                    title: ex.title,
                    subscriptionPlanIds: ex.subscriptionPlanIds ?? [],
                  });
                }}
                {...reviewableBlockViewedProp}
                {...(tourIds.stickyAudioTourId ? { stickyAudioTourId: tourIds.stickyAudioTourId } : {})}
                {...(tourIds.wordHelpTourId ? { wordHelpTourId: tourIds.wordHelpTourId } : {})}
                {...(tourIds.wordHelpScopeTourId ? { wordHelpScopeTourId: tourIds.wordHelpScopeTourId } : {})}
                {...(tourIds.tipTourId ? { tipTourId: 'tag-content-tip' } : {})}
              />
                );
              });
            })()}
            {onLastPage && (
              <LessonPracticeQuizExpandables
                exercises={exercises}
                quizzes={lessonQuizzes}
                subscriptionPlans={subscriptionPlans}
                onStartExercise={(ex) => void handleStartExercise(ex)}
                onStartQuiz={(quizId) => {
                  quizModalTargetIdRef.current = quizId;
                  setActiveQuizId(quizId);
                  setShowQuizModal(true);
                }}
              />
            )}
          </div>

          <section className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50 shadow-sm dark:border-gray-700 dark:bg-gray-800/90">
            <div className="px-3 py-1.5">
              <div className="flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className="rounded-lg bg-gray-200 p-1.5 text-gray-700 transition-colors hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  aria-label="Previous page"
                >
                  <ArrowLeft className={navArrowClass('h-4 w-4')} />
                </button>
                <span className="min-w-[4rem] text-center text-sm text-gray-600 dark:text-gray-400">
                  {currentPage + 1} / {pages.length}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(pages.length - 1, p + 1))}
                  disabled={currentPage === pages.length - 1}
                  className="rounded-lg bg-gray-200 p-1.5 text-gray-700 transition-colors hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  aria-label="Next page"
                >
                  <ArrowRight className={navArrowClass('h-4 w-4')} />
                </button>
              </div>
            </div>
          </section>
        </>
      ) : (
        <div className="space-y-4">
          {(() => {
            const blocks = pages[currentPage] ?? [];
            const blockTourIds = assignLessonBlockTourIds(blocks, locale, stickyAudioMap, 'tag-content-word-help');
            return blocks.map((block, index) => {
              const tourIds = blockTourIds[index] ?? {};
              return (
            <ContentBlockDisplay
              key={block.id || index}
              block={block}
              lessonId={lesson.id}
              pinWordHelp={blocks.length <= 1}
              lessonTypography={lessonTypography}
              lessonExercises={lesson.exercises ?? []}
              userPlanIds={userPlanIds}
              subscriptionPlans={subscriptionPlans}
              onStartInlineExercise={(exerciseId) => {
                const ex = lesson.exercises?.find((e) => e.id === exerciseId);
                if (!ex) return;
                void handleStartExercise({
                  id: ex.id,
                  title: ex.title,
                  subscriptionPlanIds: ex.subscriptionPlanIds ?? [],
                });
              }}
              {...reviewableBlockViewedProp}
              {...(tourIds.stickyAudioTourId ? { stickyAudioTourId: tourIds.stickyAudioTourId } : {})}
              {...(tourIds.wordHelpTourId ? { wordHelpTourId: tourIds.wordHelpTourId } : {})}
              {...(tourIds.wordHelpScopeTourId ? { wordHelpScopeTourId: tourIds.wordHelpScopeTourId } : {})}
              {...(tourIds.tipTourId ? { tipTourId: 'tag-content-tip' } : {})}
            />
              );
            });
          })()}
          {onLastPage && (
            <LessonPracticeQuizExpandables
              exercises={exercises}
              quizzes={lessonQuizzes}
              subscriptionPlans={subscriptionPlans}
              onStartExercise={(ex) => void handleStartExercise(ex)}
              onStartQuiz={(quizId) => {
                quizModalTargetIdRef.current = quizId;
                setActiveQuizId(quizId);
                setShowQuizModal(true);
              }}
            />
          )}
        </div>
      )}

      {showQuizModal && activeQuizId != null && (
        <QuizModal
          quizType="lesson"
          quizId={activeQuizId}
          contentForceEnglish={activeQuizForceEnglish}
          onComplete={handleQuizComplete}
          onClose={handleQuizModalClose}
          {...(locale !== 'en' && activeQuizForceEnglish && activeQuizId != null
            ? { onEnglishLockedHintClick: () => openNonFarsiHintManually('quiz', activeQuizId) }
            : {})}
          {...(lesson.previousQuizScore !== undefined ? { previousScore: lesson.previousQuizScore } : {})}
        />
      )}

      {selectedExercise && (
        <ExerciseModal
          exerciseType={selectedExercise.type}
          exerciseId={selectedExercise.id}
          contentForceEnglish={activeExerciseForceEnglish}
          onClose={() => {
            activeExerciseIdRef.current = null;
            setSelectedExercise(null);
          }}
          {...(locale !== 'en' && activeExerciseForceEnglish && selectedExercise
            ? {
                onEnglishLockedHintClick: () =>
                  openNonFarsiHintManually('exercise', activeExerciseIdRef.current ?? selectedExercise.id),
              }
            : {})}
          onLessonExerciseCompleted={() => {
            const exId = activeExerciseIdRef.current ?? selectedExercise.id;
            if (exId != null) {
              applyLocalLessonPatch((l) => patchLessonExerciseCompleted(l, exId));
            }
          }}
        />
      )}

      {lockedExerciseModal && (
        <ExerciseLockedModal
          open
          exerciseTitle={lockedExerciseModal.exerciseTitle}
          requiredPlans={lockedExerciseModal.requiredPlans}
          onClose={() => setLockedExerciseModal(null)}
        />
      )}

      <NonFarsiContentHintModal
        open={nonFarsiGateState.open}
        isRtl={nonFarsiGateRtl}
        anchorRect={nonFarsiGateState.anchorRect}
        contentKey={nonFarsiGateState.contentKey}
        onDismiss={handleNonFarsiGateDismiss}
        onContinue={handleNonFarsiGateContinue}
      />

      <CommentModal
        lessonId={lessonId}
        lessonTitle={pickUiTitle(lesson)}
        isOpen={showCommentModal}
        onClose={() => {
          setShowCommentModal(false);
          void loadLessonRatingSummary();
        }}
      />
    </div>
    </WordHelpProvider>
  );
};
